import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { auth, db, analytics } from './firebase/firebase';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { logEvent } from 'firebase/analytics';
import { useEffect } from 'react';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { EmailVerificationSuccessPage } from './pages/EmailVerificationSuccessPage';
import { PleaseVerifyEmailPage } from './pages/PleaseVerifyEmailPage';
import { UserProfilePage } from './pages/UserProfilePage';
import { CreateProfilePage } from './pages/CreateProfilePage';
import { AdminDashboard } from './pages/AdminDashboard';
import { SuperadminDashboard } from './pages/SuperadminDashboard';
import { PublicRoomsPage } from './pages/PublicRoomsPage';
import { PublicTablesPage } from './pages/PublicTablesPage';
import { TVDashboard } from './pages/TVDashboard';
import { TVDashboardSettings } from './pages/TVDashboardSettings';
import { SchedulerPage } from './pages/SchedulerPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { PublicRoute } from './components/auth/PublicRoute';
import { useAuthListener } from './hooks/useAuthListener';

// Component to track page views
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    // Track page view when location changes (only if analytics is available)
    if (analytics) {
      logEvent(analytics, 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname
      });
    }
  }, [location]);

  return null;
}

function AppContent() {
  const { user, userProfile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <>
      <PageViewTracker />
      <Routes>
        {/* Public routes */}
        <Route 
          path="/login" 
          element={
            <PublicRoute user={user}>
              <LoginPage />
            </PublicRoute>
          } 
        />
        <Route 
          path="/signup" 
          element={
            <PublicRoute user={user}>
              <SignupPage />
            </PublicRoute>
          } 
        />
        <Route 
          path="/forgot-password" 
          element={
            <PublicRoute user={user}>
              <ForgotPasswordPage />
            </PublicRoute>
          } 
        />
        <Route 
          path="/email-verification-success" 
          element={<EmailVerificationSuccessPage />}
        />
        <Route 
          path="/please-verify-email" 
          element={<PleaseVerifyEmailPage />}
        />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/how-it-works" element={<HowItWorksPage userProfile={userProfile} user={user} />} />
        <Route 
          path="/" 
          element={<PublicRoomsPage userProfile={userProfile} user={user} />}
        />
        <Route 
          path="/:roomId/tables" 
          element={<PublicTablesPage userProfile={userProfile} user={user} />}
        />
        
        {/* Profile creation route - accessible to authenticated users without profiles */}
        <Route 
          path="/create-profile" 
          element={
            <ProtectedRoute user={user} userProfile={userProfile} loading={loading}>
              <CreateProfilePage />
            </ProtectedRoute>
          } 
        />
        
        {/* Protected routes */}
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute user={user} userProfile={userProfile} loading={loading}>
              <UserProfilePage />
            </ProtectedRoute>
          } 
        />
        
        {/* Admin routes */}
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute user={user} userProfile={userProfile} loading={loading} requiredRole="admin">
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* TV Dashboard route */}
        <Route 
          path="/tv-dashboard" 
          element={
            <ProtectedRoute user={user} userProfile={userProfile} loading={loading} requiredRole="admin">
              <TVDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* TV Dashboard Settings route */}
        <Route 
          path="/tv-dashboard-settings" 
          element={
            <ProtectedRoute user={user} userProfile={userProfile} loading={loading} requiredRole="admin">
              <TVDashboardSettings />
            </ProtectedRoute>
          } 
        />
        
        {/* Scheduler route */}
        <Route 
          path="/scheduler" 
          element={
            <ProtectedRoute user={user} userProfile={userProfile} loading={loading} requiredRole="admin">
              <SchedulerPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Superadmin routes */}
        <Route 
          path="/superadmin" 
          element={
            <ProtectedRoute user={user} userProfile={userProfile} loading={loading} requiredRole="superadmin">
              <SuperadminDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  const { user, userProfile, loading } = useAuthListener(auth, db);

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AuthProvider user={user} userProfile={userProfile} loading={loading}>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
